import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { isDefined } from 'remeda';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useLegacyAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';
import type { ReminderDocumentType } from './preview-reminder-request';

// #region Response
const ReminderTemplatesResponseSchema = z.object({
  data: z.array(
    z.object({
      id: z.coerce.string(),
      attributes: z.object({
        name: z.string(),
      }),
    }),
  ),
});

export type ReminderTemplate = {
  id: string;
  name: string;
};
// #endregion Response

const PLACEHOLDER: Array<ReminderTemplate> = [];

// MARK: Load function
async function loadReminderTemplatesQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  documentType: ReminderDocumentType,
): Promise<Array<ReminderTemplate>> {
  const instance = await axiosInstance;
  const { data } = await instance.get('/templates', {
    params: { step_type: documentType },
  });

  const parseResult = ReminderTemplatesResponseSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'reminderTemplates');
    throw new SilentError();
  }

  return parseResult.data.data.map(({ id, attributes }) => ({ id, name: attributes.name }));
}

// MARK: Hook
export function useLoadReminderTemplates(
  documentType: ReminderDocumentType,
  options: QueryOptions<Array<ReminderTemplate>> = {},
) {
  const axiosInstance = useLegacyAxiosInstance();

  const queryResult = useQuery<Array<ReminderTemplate>, ApiError>({
    queryKey: ['reminderTemplates', documentType],
    queryFn: () => loadReminderTemplatesQueryFn(axiosInstance, documentType),
    placeholderData: previousData => (isDefined(previousData) ? previousData : PLACEHOLDER),
    ...options,
  });

  return addResourceNameToQueryResult<
    Array<ReminderTemplate>,
    ApiError,
    'reminderTemplates',
    'PreventUndefined'
  >('reminderTemplates', queryResult, PLACEHOLDER);
}
